import React, { useState, useEffect, useRef } from "react";
import { fetchRecruitment } from "../api";
import "./Dashboard.css";

const ROLES = ["CNA", "LPN", "RN"];

const Logic = ({ children }) => (
  <p className="calc-logic"><b>How this is calculated:</b> {children}</p>
);

export default function Recruitment({ ctx, passcode }) {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const retry = useRef(null);

  const load = (m) => {
    setLoading(true); setErr(null);
    fetchRecruitment(ctx, passcode, m)
      .then((r) => {
        if (r.warming) { setData({ warming: true }); retry.current = setTimeout(() => load(m), 8000); return; }
        setData(r); setMonth(r.month);
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    return () => clearTimeout(retry.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (err) return <div className="state error">Error: {err}</div>;
  if (data?.warming) {
    return (
      <section className="panel" style={{ textAlign: "center", padding: "36px 20px" }}>
        <div style={{ fontSize: 28, marginBottom: 6 }}>⏳</div>
        <h2>Loading recruitment data…</h2>
        <p className="muted">The first load pulls all shifts from Teambridge; this refreshes automatically.</p>
      </section>
    );
  }
  if (loading && !data) return <div className="state">Loading recruitment…</div>;
  if (!data) return null;

  const h = data.hiresByMonth, s = data.shiftBands, rm = data.rosterMovement, f = data.future;
  const monthOpts = data.months || [];
  const labelFor = (m) => { const [y, mo] = m.split("-").map(Number); return new Date(Date.UTC(y, mo - 1, 1)).toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" }); };

  return (
    <div className={loading ? "dim" : ""}>
      {/* Report 1 — trailing 12 months */}
      <section className="panel">
        <div className="panel-head"><h2>Hires by Month</h2><span>clinicians · trailing 12 months</span></div>
        <div className="pl-scroll">
          <table className="grid">
            <thead><tr><th>Month</th>{ROLES.map((r) => <th key={r}>{r}</th>)}<th className="strong">Total</th></tr></thead>
            <tbody>
              {h.rows.map((row) => (
                <tr key={row.month}><td className="rowhead">{row.label}</td>{ROLES.map((r) => <td key={r}>{row[r]}</td>)}<td className="strong">{row.total}</td></tr>
              ))}
              <tr className="total-row"><td className="rowhead">12-month total</td>{ROLES.map((r) => <td key={r}>{h.totals[r]}</td>)}<td className="strong">{h.totals.total}</td></tr>
            </tbody>
          </table>
        </div>
        <Logic>{h.logic}</Logic>
      </section>

      {/* Month filter for reports 2 & 3 */}
      <div className="month-filter">
        <span>Reports below — month:</span>
        <select className="month-select" value={month || data.month} onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}>
          {monthOpts.map((m) => <option key={m} value={m}>{labelFor(m)}</option>)}
        </select>
      </div>

      {/* Report 2 — shift bands for the month */}
      <section className="panel">
        <div className="panel-head"><h2>Shifts Worked — {s.monthLabel}</h2><span>published shifts · Patrick demo excluded</span></div>
        <div className="pl-scroll">
          <table className="grid">
            <thead><tr><th>Clinician type</th>{s.columns.map((c) => <th key={c}>{c} shifts</th>)}<th className="strong">Total</th></tr></thead>
            <tbody>
              {s.rows.map((row) => (
                <tr key={row.role}><td className="rowhead">{row.role}</td>{s.columns.map((c) => <td key={c}>{row[c]}</td>)}<td className="strong">{row.total}</td></tr>
              ))}
              <tr className="total-row"><td className="rowhead">Total employees</td>{s.columns.map((c) => <td key={c}>{s.colTotals[c]}</td>)}<td className="strong">{s.grandTotal}</td></tr>
            </tbody>
          </table>
        </div>
        <Logic>{s.logic}</Logic>
      </section>

      {/* Report 3 — roster movement for the month */}
      <section className="panel">
        <div className="panel-head"><h2>Roster Movement — {rm.monthLabel}</h2><span>who joined vs. who left</span></div>
        <div className="pl-scroll">
          <table className="grid">
            <thead><tr><th>Clinician type</th><th>Added (hired)</th><th>Removed (made inactive)</th><th className="strong">Net change</th></tr></thead>
            <tbody>
              {rm.rows.map((row) => (
                <tr key={row.role}>
                  <td className="rowhead">{row.role}</td>
                  <td className="pos-text">{row.added ? `+${row.added}` : 0}</td>
                  <td className="neg-text">{row.removed ? `−${row.removed}` : 0}</td>
                  <td className={"strong " + (row.net > 0 ? "pos-text" : row.net < 0 ? "neg-text" : "")}>{row.net > 0 ? `+${row.net}` : row.net}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td className="rowhead">Total</td>
                <td className="pos-text">{rm.totals.added ? `+${rm.totals.added}` : 0}</td>
                <td className="neg-text">{rm.totals.removed ? `−${rm.totals.removed}` : 0}</td>
                <td className={"strong " + (rm.totals.net > 0 ? "pos-text" : rm.totals.net < 0 ? "neg-text" : "")}>{rm.totals.net > 0 ? `+${rm.totals.net}` : rm.totals.net}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <Logic>{rm.logic}</Logic>

        <h3 className="exec-sub" style={{ marginTop: 20 }}>Caregivers with an upcoming shift (current)</h3>
        <div className="pl-scroll">
          <table className="grid">
            <thead><tr><th>Clinician type</th><th>With a future shift</th><th>Active caregivers</th><th className="strong">%</th></tr></thead>
            <tbody>
              {f.rows.map((row) => (
                <tr key={row.role}><td className="rowhead">{row.role}</td><td>{row.withFuture}</td><td>{row.total}</td><td className="strong">{row.pct}%</td></tr>
              ))}
              <tr className="total-row"><td className="rowhead">Total</td><td>{f.total.withFuture}</td><td>{f.total.total}</td><td className="strong">{f.total.pct}%</td></tr>
            </tbody>
          </table>
        </div>
        <Logic>{f.logic}</Logic>
      </section>

      <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>Live from Teambridge · as of {data.asOf}</p>
    </div>
  );
}
